import React, { useState, useRef, useEffect } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  VStack,
  HStack,
  Input,
  Badge,
  IconButton,
  Text,
  Wrap,
  WrapItem,
  List,
  Field,
} from '@chakra-ui/react';
import {
  FaTimes,
  FaPlus,
} from 'react-icons/fa';

const SkillsEditor = ({ 
  title,
  helperText,
  placeholder,
  skills = [],
  onSkillsChange,
  suggestions = [],
  maxSkills = 10
}) => {
  const [currentSkills, setCurrentSkills] = useState(skills);
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  
  useEffect(() => {
    if (!showSuggestions) return undefined;
    const onPointerDown = (event) => {
      const root = suggestionsRef.current;
      if (root && !root.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [showSuggestions]);

  useEffect(() => {
    setCurrentSkills(skills);
  }, [skills]);

  useEffect(() => {
    if (inputValue.length > 0) {
      const filtered = suggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
        !currentSkills.some((skill) => String(skill).toLowerCase() === suggestion.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
  }, [inputValue, suggestions, currentSkills]);


  const addSkill = (skill) => {
    const trimmedSkill = skill.trim();
    if (trimmedSkill && !currentSkills.some((s) => String(s).toLowerCase() === trimmedSkill.toLowerCase()) && currentSkills.length < maxSkills) {
      const newSkills = [...currentSkills, trimmedSkill];
      setCurrentSkills(newSkills);
      onSkillsChange(newSkills); // Actualizar padre inmediatamente
      setInputValue('');
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const removeSkill = (skillToRemove) => {
    const newSkills = currentSkills.filter(skill => skill !== skillToRemove);
    setCurrentSkills(newSkills);
    onSkillsChange(newSkills); // Actualizar padre inmediatamente
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && filteredSuggestions[selectedSuggestionIndex]) {
        addSkill(filteredSuggestions[selectedSuggestionIndex]);
      } else if (inputValue.trim()) {
        addSkill(inputValue);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    addSkill(suggestion);
  };

  return (
    <VStack gap={4} align="stretch">
      <Field.Root>
        <Field.Label>{title}</Field.Label>
        <Box position="relative" ref={suggestionsRef}>
          <HStack gap={2}>
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              disabled={currentSkills.length >= maxSkills}
              size="lg"
              rounded="xl"
              bg="gray.50"
              border="2px"
              borderColor="gray.200"
              _hover={{ borderColor: 'gray.300' }}
              _focus={{ borderColor: 'purple.500', bg: 'white' }}
            />
            <IconButton
              size="lg"
              bg="purple.600"
              color="white"
              rounded="full"
              onClick={() => addSkill(inputValue)}
              disabled={!inputValue.trim() || currentSkills.length >= maxSkills}
              aria-label="Añadir"
              _hover={{ bg: 'purple.700', transform: 'scale(1.05)' }}
              _active={{ transform: 'scale(0.95)' }}><FaPlus /></IconButton>
          </HStack>
          
          {/* Sugerencias */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              zIndex={10}
              bg={bgColor}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              shadow="xl"
              maxH="200px"
              overflowY="auto"
              mt={1}
            >
              <List.Root gap={0}>
                {filteredSuggestions.map((suggestion, index) => (
                  <List.Item
                    key={index}
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg={selectedSuggestionIndex === index ? hoverBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                    onClick={() => handleSuggestionClick(suggestion)}
                    fontSize="sm"
                  >
                    {suggestion}
                  </List.Item>
                ))}
              </List.Root>
            </Box>
          )}
        </Box>
        
        <Field.HelperText>
          {helperText} ({currentSkills.length}/{maxSkills})
        </Field.HelperText>
      </Field.Root>

      {/* Skills actuales */}
      {currentSkills.length > 0 && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            {title} añadidas:
          </Text>
          <Wrap gap={2}>
            {currentSkills.map((skill, index) => (
              <WrapItem key={index}>
                <Badge
                  bg="purple.100"
                  color="purple.700"
                  px={4}
                  py={2}
                  rounded="full"
                  fontSize="sm"
                  fontWeight="600"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  {skill}
                  <IconButton
                    size="xs"
                    variant="ghost"
                    onClick={() => removeSkill(skill)}
                    aria-label={`Eliminar ${skill}`}
                    minW="auto"
                    h="auto"
                    p={0}
                    color="purple.600"
                    _hover={{ bg: 'transparent', color: 'red.500' }}><FaTimes /></IconButton>
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}
    </VStack>
  );
};

export default SkillsEditor;
